import { useRef, useState } from "react";
import { IconChevronDown, IconDownload, IconFileImport, IconFileSpreadsheet } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import * as XLSX from "xlsx";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Group,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@/components/ui";
import { exportarExcel } from "@/shared/export/exportar-excel";
import { toastAdvertencia } from "@/lib/toast";
import type {
  FilaImportacionItemCatalogo,
  PreviewFilaItemCatalogo,
  ResultadoImportacionItemCatalogo,
} from "../services/catalogos-contables-admin-service";

/** Encabezados aceptados por columna (normalizados: minúsculas, sin acentos ni separadores). */
const CODIGO_HEADERS = ["codigo", "cuenta", "ceco", "clave", "centrodecosto", "centrocosto", "cuentacontable"];
const NOMBRE_HEADERS = ["nombre", "descripcion"];
const SOCIEDAD_HEADERS = ["numerosociedad", "sociedad", "nosociedad", "numsociedad", "organizacion", "numerodesociedad"];

// Quita acentos también, para que "Código" (encabezado bonito de la plantilla) matchee "codigo".
const norm = (s: string) =>
  s.trim().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[\s._-]/g, "");

function extraer(fila: Record<string, unknown>, candidatos: string[]): string {
  for (const [k, v] of Object.entries(fila)) {
    if (candidatos.includes(norm(k))) return String(v ?? "").trim();
  }
  return "";
}

interface Props {
  /** La mutation de importación REAL (useImportarCuentas / useImportarCecos). */
  importar: {
    mutate: (filas: FilaImportacionItemCatalogo[], options?: { onSuccess?: () => void }) => void;
    isPending: boolean;
  };
  /** Dry-run (soloValidar=true) para el preview · devuelve el detalle por fila. */
  previsualizar: (filas: FilaImportacionItemCatalogo[]) => Promise<ResultadoImportacionItemCatalogo>;
  /** Nombre base del archivo de plantilla (sin extensión). */
  plantillaArchivo: string;
  /** Nombre de la hoja de la plantilla. */
  plantillaHoja: string;
  /** Filas de ejemplo dentro de la plantilla. */
  ejemplos: FilaImportacionItemCatalogo[];
  /**
   * Cuando true, la plantilla/parseo/preview incluyen la columna "Número de sociedad"
   * (Centros de Costo · relación con organización M:N). Un mismo código puede repetirse
   * en varias filas con distinta sociedad. Para Cuentas se omite.
   */
  incluyeSociedad?: boolean;
}

/**
 * Control "Excel" unificado (importar + descargar plantilla) para un catálogo
 * de Código + Nombre (Cuentas contables / Centros de costo). Antes de persistir
 * muestra una VISTA PREVIA (dry-run) con qué se hará por fila (crear/actualizar/
 * omitir) · mismo patrón que el gestor de Cuentas por clave SAT.
 */
export function ImportarExcelCatalogo({ importar, previsualizar, plantillaArchivo, plantillaHoja, ejemplos, incluyeSociedad = false }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [filas, setFilas] = useState<FilaImportacionItemCatalogo[]>([]);
  const [preview, setPreview] = useState<ResultadoImportacionItemCatalogo | null>(null);

  function descargarPlantilla() {
    exportarExcel<FilaImportacionItemCatalogo>({
      archivo: plantillaArchivo,
      hoja: plantillaHoja,
      columnas: [
        { header: "Código", value: (f) => f.codigo },
        { header: "Nombre", value: (f) => f.nombre },
        ...(incluyeSociedad
          ? [{ header: "Número de sociedad", value: (f: FilaImportacionItemCatalogo) => f.numeroSociedad ?? "" }]
          : []),
      ],
      filas: ejemplos,
    });
  }

  const validar = useMutation({
    mutationFn: (fs: FilaImportacionItemCatalogo[]) => previsualizar(fs),
    onSuccess: (res) => setPreview(res),
    onError: () => toastAdvertencia({ title: "No se pudo validar el archivo", message: "Intenta de nuevo." }),
  });

  const cerrar = () => {
    setPreview(null);
    setFilas([]);
  };

  async function onArchivo(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // permite re-seleccionar el mismo archivo
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf);
      const sheet = wb.Sheets[wb.SheetNames[0]];
      if (!sheet) {
        toastAdvertencia({ title: "Archivo vacío", message: "El Excel no tiene hojas con datos." });
        return;
      }
      const crudas = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: "" });
      const parsed: FilaImportacionItemCatalogo[] = crudas
        .map((r) => ({
          codigo: extraer(r, CODIGO_HEADERS),
          nombre: extraer(r, NOMBRE_HEADERS),
          ...(incluyeSociedad ? { numeroSociedad: extraer(r, SOCIEDAD_HEADERS) } : {}),
        }))
        .filter((f) => f.codigo);

      if (parsed.length === 0) {
        toastAdvertencia({
          title: "Sin filas válidas",
          message: "Revisa que exista la columna Código con datos (usa la plantilla).",
        });
        return;
      }
      setFilas(parsed);
      validar.mutate(parsed);
    } catch {
      toastAdvertencia({ title: "No se pudo leer el archivo", message: "¿Es un .xlsx/.xls válido?" });
    }
  }

  const aImportar = preview ? preview.creados + preview.actualizados : 0;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="default"
            leftSection={<IconFileSpreadsheet size={14} />}
            rightSection={<IconChevronDown size={14} />}
            loading={validar.isPending || importar.isPending}
          >
            Excel
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem onSelect={() => inputRef.current?.click()}>
            <IconFileImport size={16} stroke={1.7} />
            Importar datos en Excel
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={descargarPlantilla}>
            <IconDownload size={16} stroke={1.7} />
            Descargar plantilla
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls"
        onChange={(e) => void onArchivo(e)}
        style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
        tabIndex={-1}
        aria-hidden="true"
      />

      <Dialog open={preview !== null} onOpenChange={(o) => !o && cerrar()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Vista previa de importación</DialogTitle>
          </DialogHeader>
          {preview && (
            <Stack gap="sm">
              <Group gap="xs">
                <Badge color="exito">{preview.creados} a crear</Badge>
                <Badge color="info">{preview.actualizados} a actualizar</Badge>
                <Badge color={preview.omitidos > 0 ? "peligro" : "slate"}>{preview.omitidos} a omitir</Badge>
              </Group>
              <Text size="xs" c="dimmed">
                {aImportar > 0
                  ? "Revisa las filas antes de confirmar. Sólo se aplicarán las de crear/actualizar; las omitidas se ignoran."
                  : "Ninguna fila es importable (revisa los motivos). Corrige el archivo y vuelve a intentar."}
              </Text>
              <div className="max-h-[50vh] overflow-auto rounded-md border border-border">
                <Table verticalSpacing="xs" horizontalSpacing="sm">
                  <Thead>
                    <Tr>
                      <Th>#</Th>
                      <Th>Código</Th>
                      <Th>Nombre</Th>
                      {incluyeSociedad ? <Th>Sociedad</Th> : null}
                      <Th>Acción</Th>
                      <Th>Motivo</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {preview.detalle.map((f, i) => (
                      <FilaPreview key={`${f.renglon}-${f.codigo}-${f.numeroSociedad ?? i}`} fila={f} incluyeSociedad={incluyeSociedad} />
                    ))}
                  </Tbody>
                </Table>
              </div>
            </Stack>
          )}
          <DialogFooter>
            <Button variant="default" onClick={cerrar}>
              Cancelar
            </Button>
            <Button
              loading={importar.isPending}
              disabled={aImportar === 0}
              onClick={() => importar.mutate(filas, { onSuccess: cerrar })}
            >
              Confirmar importación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

const COLOR_ESTADO: Record<PreviewFilaItemCatalogo["estado"], string> = {
  Crear: "exito",
  Actualizar: "info",
  Omitir: "peligro",
};

function FilaPreview({ fila, incluyeSociedad }: { fila: PreviewFilaItemCatalogo; incluyeSociedad?: boolean }) {
  return (
    <Tr>
      <Td className="font-mono">{fila.renglon}</Td>
      <Td>
        <Text size="xs" ff="mono">
          {fila.codigo}
        </Text>
      </Td>
      <Td>
        <Text size="sm">{fila.nombre || "—"}</Text>
      </Td>
      {incluyeSociedad ? (
        <Td>
          <Text size="xs" ff="mono">
            {fila.numeroSociedad || "—"}
          </Text>
        </Td>
      ) : null}
      <Td>
        <Badge size="sm" color={COLOR_ESTADO[fila.estado]}>
          {fila.estado}
        </Badge>
      </Td>
      <Td>
        <Text size="xs" c={fila.motivo ? "peligro" : "dimmed"}>
          {fila.motivo ?? "—"}
        </Text>
      </Td>
    </Tr>
  );
}
